import { motion } from "framer-motion";
import { ChevronDown, Search } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export const Select = ({
  value,
  onChange,
  options,
  optionValue = "value",
  optionLabel = "label",
  placeholder = "Select...",
  className = "",
  direction = "auto",
  searchable = false,
  popoverWidth = null,
  filteredOptions = null,
  searchTerm: externalSearchTerm = "",
  onSearchChange = null,
  onSelect = null,
  zIndex = 9999,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const [internalSearchTerm, setInternalSearchTerm] = useState("");
  const [popoverDirection, setPopoverDirection] = useState(direction);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Constants for calculations
  const POPOVER_MAX_HEIGHT = 248; // Height of dropdown (200px) + padding
  const VIEWPORT_PADDING = 20; // Minimum padding from viewport edges

  // Use external or internal search term based on whether external control is provided
  const searchTerm = onSearchChange ? externalSearchTerm : internalSearchTerm;
  const handleSearchChange = (value) => {
    if (onSearchChange) {
      onSearchChange(value);
    } else {
      setInternalSearchTerm(value);
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update position when dropdown opens or window resizes
  useEffect(() => {
    const updatePosition = () => {
      if (triggerRef.current && isOpen) {
        const rect = triggerRef.current.getBoundingClientRect();
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;

        // Calculate space available below and above the trigger
        const spaceBelow = windowHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Determine popover direction based on available space if auto mode
        let calculatedDirection = direction;
        if (direction === "auto") {
          // If there's not enough space below but enough space above
          if (spaceBelow < POPOVER_MAX_HEIGHT && spaceAbove > POPOVER_MAX_HEIGHT) {
            calculatedDirection = "top";
          } else {
            calculatedDirection = "bottom";
          }
        }

        setPopoverDirection(calculatedDirection);

        // Calculate position based on direction
        const topPosition =
          calculatedDirection === "top"
            ? rect.top + scrollTop - VIEWPORT_PADDING // Position above with some padding
            : rect.bottom + scrollTop;

        // Calculate position
        setPosition({
          top: topPosition,
          left: rect.left + scrollLeft,
          width: rect.width,
          spaceBelow,
          spaceAbove,
        });
      }
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, direction]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else if (!isOpen) {
      handleSearchChange("");
    }
  }, [isOpen, searchable]);

  // Find the selected option label
  const selectedOption = options.find((option) => option[optionValue] === value);
  const displayValue = selectedOption
    ? typeof optionLabel === "function"
      ? optionLabel(selectedOption)
      : selectedOption[optionLabel]
    : placeholder;

  // Filter options based on search term (only if filtered options aren't provided externally)
  const displayedOptions =
    filteredOptions !== null
      ? filteredOptions
      : searchable && searchTerm
        ? options.filter((option) => {
            const label =
              typeof optionLabel === "function" ? optionLabel(option) : option[optionLabel];
            return label.toLowerCase().includes(searchTerm.toLowerCase());
          })
        : options;

  // Calculate popover width
  const calculatedPopoverWidth = popoverWidth ? popoverWidth : position.width * 1.5; // 50% wider than the trigger by default

  // Handle option select
  const handleOptionSelect = (option) => {
    onChange(option);
    setIsOpen(false);

    // Call onSelect callback if provided
    if (onSelect) {
      setTimeout(() => onSelect(option), 0);
    }
  };

  return (
    <>
      <div className={`tw-relative ${className}`} ref={triggerRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`tw-group tw-flex tw-h-12 tw-w-full tw-items-center tw-justify-between tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-px-3 tw-transition-all tw-duration-200 ${
            isOpen ? "tw-border-blue-500 tw-ring-1 tw-ring-blue-500" : "hover:tw-border-gray-300"
          }`}
        >
          <span className="tw-truncate tw-font-medium tw-text-gray-900">{displayValue}</span>
          <ChevronDown
            className={`tw-h-4 tw-w-4 tw-text-gray-400 tw-transition-transform tw-duration-200 ${
              isOpen ? "tw-rotate-180" : ""
            }`}
          />
        </button>
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "absolute",
              top: `${position.top}px`,
              left: `${position.left}px`,
              width: `${searchable ? calculatedPopoverWidth : position.width}px`,
              zIndex,
              ...(popoverDirection === "top" ? { transform: "translateY(-100%)" } : {}),
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: popoverDirection === "top" ? 10 : -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: popoverDirection === "top" ? 10 : -10 }}
              transition={{ duration: 0.2 }}
              className={`tw-w-full tw-overflow-hidden tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg ${
                popoverDirection === "top" ? "tw-mb-1" : "tw-mt-1"
              }`}
            >
              {searchable && (
                <div className="tw-border-b tw-border-gray-100 tw-p-2">
                  <div className="tw-relative">
                    <div className="tw-absolute tw-left-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400">
                      <Search className="tw-h-4 tw-w-4" />
                    </div>
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchTerm}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      placeholder="Search..."
                      className="tw-h-9 tw-w-full tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 tw-pl-9 tw-pr-3 tw-text-sm tw-outline-none tw-transition-all tw-duration-200 focus:tw-border-blue-500 focus:tw-bg-white focus:tw-ring-1 focus:tw-ring-blue-500"
                    />
                  </div>
                </div>
              )}

              <div
                className="tw-overflow-auto"
                style={{
                  maxHeight:
                    popoverDirection === "top"
                      ? `${Math.min(position.spaceAbove - VIEWPORT_PADDING * 2, 200)}px`
                      : `${Math.min(position.spaceBelow - VIEWPORT_PADDING * 2, 200)}px`,
                }}
              >
                {displayedOptions.length > 0 ? (
                  displayedOptions.map((option) => (
                    <button
                      type="button"
                      key={option[optionValue]}
                      onClick={() => handleOptionSelect(option)}
                      className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-px-4 tw-py-2.5 tw-text-left tw-transition-colors hover:tw-bg-gray-50"
                    >
                      <span className="tw-text-gray-900">
                        {typeof optionLabel === "function"
                          ? optionLabel(option)
                          : option[optionLabel]}
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="tw-px-4 tw-py-3 tw-text-center tw-text-sm tw-text-gray-500">
                    No options found
                  </div>
                )}
              </div>
            </motion.div>
          </div>,
          document.body
        )}
    </>
  );
};
